import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICorporationResponse } from '@kcftech/prometheustypescriptapiclient';
import { AuthorizedImage } from 'components/image/AuthorizedImage';
import { memo, ReactNode } from 'react';
import styled from 'styled-components';
import { secondaryText } from 'styles/theme';

import { Header, HeaderControls, QuickFilterHeaderRegion } from './styled';

export type DashboardHeaderProps = Readonly<{
    /** Corporation object containing logo and name */
    corporation: ICorporationResponse;
    /** Optional subtitle text to display after corporation name */
    subtitle?: string;
    /** Optional icon to display with subtitle */
    subtitleIcon?: IconDefinition;
    /** Filter buttons to render in the header controls */
    children: ReactNode;
}>;

const LogoImage = styled(AuthorizedImage)`
    max-height: 4.5rem;
    max-width: 12rem;
`;

const LogoWrapper = styled.span`
    display: inline-flex;
    width: 4rem;
    height: 4rem;
    max-width: 8rem;
    align-items: center;
    justify-content: center;
`;

const Title = styled.h1`
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 1rem;
`;

const Subtitle = styled.span`
    color: ${secondaryText};
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
`;

/**
 * Reusable header component for dashboard pages
 *
 * Displays:
 * - Corporation logo (with globe fallback)
 * - Corporation name
 * - Optional subtitle with icon (in secondaryText color)
 * - Filter buttons in QuickFilterHeaderRegion
 *
 * @example
 * ```tsx
 * <DashboardHeader
 *     corporation={corporation}
 *     subtitle="Desk Activity"
 *     subtitleIcon={faColumns}
 * >
 *     <SeverityFilterButton {...props} />
 *     <LocationFilterButton {...props} />
 *     <TimeRangeFilterButton {...props} />
 * </DashboardHeader>
 * ```
 */
export const DashboardHeader = memo(function DashboardHeader({
    corporation,
    subtitle,
    subtitleIcon,
    children,
}: DashboardHeaderProps) {
    return (
        <Header className="head">
            <Title>
                <LogoWrapper>
                    <LogoImage
                        src={corporation.IconUrl}
                        aria-label={`Logo for ${corporation.Name}`}
                        alt={`${corporation.Name} logo`}
                        className="corporation-logo"
                        fallback={
                            <FontAwesomeIcon
                                className="f-18 cursor-pointer"
                                icon={faGlobe}
                            />
                        }
                        imageScaling="contain"
                    />
                </LogoWrapper>
                {corporation.Name}
                {subtitle && (
                    <Subtitle>
                        {subtitleIcon && <FontAwesomeIcon icon={subtitleIcon} />}
                        {subtitle}
                    </Subtitle>
                )}
            </Title>

            <HeaderControls>
                <QuickFilterHeaderRegion>{children}</QuickFilterHeaderRegion>
            </HeaderControls>
        </Header>
    );
});
